import type { Task, TaskListItem } from "@myapp/contract";
import { EntitySearchSelect } from "../../../shared/ui/search/EntitySearchSelect/EntitySearchSelect";
import { useTasksInfiniteQuery } from "../api/queries/useTasksInfiniteQuery";
import { useTaskDetail } from "../api/queries/useTaskDetail";

/**
 * Pick one task by searching its title. Twin of `entities/user`'s `HandwerkerSearchSelect`.
 *
 * `TaskFilters` calls the title search `title`, not `search` — the list endpoint filters on the
 * field rather than across the row, so a technician's name will not find their tasks here.
 */

/** The subset both the list rows and the detail response satisfy, so one label formats both. */
interface TaskItem {
  id: number;
  title: string;
  assignedTechnician?: { fullName: string } | null;
  schedule?: { plannedWorkStartsAt: string } | null;
}

const formatWhen = (iso: string) =>
  new Date(iso).toLocaleString("de-DE", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

/** Everything under the title that tells two similarly named tasks apart. */
const describeTask = (task: TaskItem) =>
  [
    task.assignedTechnician?.fullName,
    task.schedule ? formatWhen(task.schedule.plannedWorkStartsAt) : "Nicht geplant",
  ]
    .filter(Boolean)
    .join(" · ");

interface Props {
  value: number | null | undefined;
  onChange: (id: number | null) => void;
  disabled?: boolean;
  /** Validation message, passed straight through — see `EntitySearchSelect.error`. */
  error?: string | null;
  /** A `Controller`'s `field.ref`, so react-hook-form can focus this field on a failed submit. */
  inputRef?: React.Ref<HTMLInputElement | HTMLTextAreaElement>;
  className?: string;
  placeholder?: string;
}

export function TaskSearchSelect({
  value,
  onChange,
  disabled,
  error,
  inputRef,
  className,
  placeholder = "Aufgabe suchen…",
}: Props) {
  const useAdaptedInfiniteHook = (params: any, options?: { enabled?: boolean }) => {
    const result = useTasksInfiniteQuery(params, options);
    return {
      ...result,
      data: result.data
        ? {
            ...result.data,
            items: result.data.items.map(
              (task: TaskListItem): TaskItem => ({
                id: task.id,
                title: task.title,
                assignedTechnician: task.assignedTechnician,
                schedule: task.schedule,
              }),
            ),
          }
        : result.data,
    };
  };

  // `useTaskDetail` carries its own `enabled: !!id`, so the options argument has nothing to add.
  const useAdaptedDetailHook = (id: number | undefined) => {
    const result = useTaskDetail(id);
    const task = result.data as Task | undefined;
    return {
      ...result,
      data: task
        ? {
            id: task.id,
            title: task.title,
            assignedTechnician: task.assignedTechnician,
            schedule: task.schedule,
          }
        : undefined,
    };
  };

  return (
    <EntitySearchSelect<TaskItem>
      value={value}
      onChange={(id) => onChange(id)}
      useInfiniteHook={useAdaptedInfiniteHook}
      useDetailHook={useAdaptedDetailHook}
      searchParamName="title"
      getLabel={(task) => task.title}
      renderItem={(task) => (
        <div className="flex flex-col gap-0.5">
          <span>{task.title}</span>
          <span className="text-[12px] leading-[15px] text-gray-500">{describeTask(task)}</span>
        </div>
      )}
      placeholder={placeholder}
      disabled={disabled}
      error={error}
      inputRef={inputRef}
      className={className}
    />
  );
}
